/**
 * Filter configuration and utilities for AI Block activity view.
 */

import _ from 'lodash'
import type { ExtendedBlockEvent } from './ai-block-activity-types'

export type FilterValue = string | number | boolean

export interface IFilterOption {
  value: FilterValue
  label: string
  count?: number
}

export interface IFilterConfig {
  id: string
  label: string
  type: 'multiselect' | 'select' | 'search'
  options?: IFilterOption[]
  getValueFromExecution: (execution: ExtendedBlockEvent) => FilterValue | FilterValue[]
}

export interface IActiveFilters {
  [filterId: string]: FilterValue[]
}

/**
 * Predefined filter configurations
 * 
 * To add a new filter:
 * 1. Add a new IFilterConfig object to this array with:
 *    - id: Unique identifier for the filter
 *    - label: Display label shown in the UI
 *    - type: Currently supports 'multiselect'
 *    - getValueFromExecution: Function that extracts the filter value from an execution
 * 
 * 2. (Optional) Add custom label formatting in formatFilterLabel() function below
 * 
 * Example: To add an "Action" filter:
 * {
 *   id: 'action',
 *   label: 'Action',
 *   type: 'multiselect',
 *   getValueFromExecution: (execution) => execution.result?.action || 'unknown',
 * }
 */
export const FILTER_CONFIGS: IFilterConfig[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'multiselect',
    getValueFromExecution: (execution) => execution.status || 'unknown',
  },
  {
    id: 'aiBlock',
    label: 'AI Block',
    type: 'multiselect',
    getValueFromExecution: (execution) =>
      execution.aiBlock?.displayName || execution.aiBlock?.entityId || 'Unknown',
  },
]

/**
 * Generate filter options from executions dynamically
 */
export function generateFilterOptions(
  executions: ExtendedBlockEvent[],
  filterId: string,
  getValueFromExecution: (execution: ExtendedBlockEvent) => FilterValue | FilterValue[]
): IFilterOption[] {
  const valueCounts = new Map<FilterValue, number>()

  executions.forEach((execution) => {
    const value = getValueFromExecution(execution)
    const values = Array.isArray(value) ? value : [value]

    values.forEach((v) => {
      valueCounts.set(v, (valueCounts.get(v) || 0) + 1)
    })
  })

  // Sort by count descending, then alphabetically
  return Array.from(valueCounts.entries())
    .sort(([a, countA], [b, countB]) => {
      if (countA !== countB) return countB - countA
      return String(a).localeCompare(String(b))
    })
    .map(([value, count]) => ({
      value,
      label: formatFilterLabel(filterId, value),
      count,
    }))
}

/**
 * Format filter labels for display
 */
function formatFilterLabel(filterId: string, value: FilterValue): string {
  if (filterId === 'status') {
    return _.startCase(String(value))
  }

  return String(value)
}

/**
 * Apply active filters to executions list
 */
export function applyFilters(
  executions: ExtendedBlockEvent[],
  activeFilters: IActiveFilters,
  filterConfigs: IFilterConfig[]
): ExtendedBlockEvent[] {
  return executions.filter((execution) => {
    // For each filter, check if the execution matches
    return filterConfigs.every((config) => {
      const activeValues = activeFilters[config.id]

      // If no values selected for this filter, include all
      if (!activeValues || activeValues.length === 0) {
        return true
      }

      // Get the value(s) from the execution
      const executionValue = config.getValueFromExecution(execution)
      const executionValues = Array.isArray(executionValue) ? executionValue : [executionValue]

      // Check if any execution value matches any active filter value
      return executionValues.some((v) => activeValues.includes(v))
    })
  })
}

/**
 * Get count of active filters
 */
export function getActiveFilterCount(activeFilters: IActiveFilters): number {
  return Object.values(activeFilters).reduce((sum, values) => sum + values.length, 0)
}
